'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';

const LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Great',
  5: 'Excellent',
};

interface StarRatingProps {
  value: number | null;
  onChange: (score: number) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function StarRating({ value, onChange, disabled, size = 'md' }: StarRatingProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const active = hovered ?? value ?? 0;
  const sizeClass = size === 'sm' ? 'text-xl' : size === 'lg' ? 'text-4xl' : 'text-3xl';

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`${n} — ${LABELS[n]}`}
            aria-pressed={value === n}
            disabled={disabled}
            className={cn(
              sizeClass,
              'transition-transform duration-100 hover:scale-110 focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              active >= n ? 'text-amber-400' : 'text-gray-300',
            )}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => {
              // Toggle off if same star clicked
              if (value === n) onChange(0);
              else onChange(n);
            }}
          >
            ★
          </button>
        ))}
      </div>
      {active > 0 && (
        <span className="text-xs text-muted-foreground">{LABELS[active]}</span>
      )}
    </div>
  );
}

interface DisplayStarsProps {
  score: number;
  size?: 'xs' | 'sm' | 'md';
  showLabel?: boolean;
}

export function DisplayStars({ score, size = 'sm', showLabel }: DisplayStarsProps) {
  const sizeClass = size === 'xs' ? 'text-sm' : size === 'md' ? 'text-xl' : 'text-base';
  return (
    <span className={cn('flex items-center gap-0.5', sizeClass)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= Math.round(score) ? 'text-amber-400' : 'text-gray-300'}>
          ★
        </span>
      ))}
      {showLabel && (
        <span className="ml-1 text-xs text-muted-foreground">{score.toFixed(1)}</span>
      )}
    </span>
  );
}
